<script setup lang="ts">
import { myRequest } from '@/utils/fetch'
import { ref, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import OGHeader from '@/components/OGHeader.vue'
import UpdatePersonDialog from '@/components/UpdatePersonDialog.vue'
export type Person = {
  personID: number
  userID: number
  companyID: number
  firstName: string
  lastName: string
  email: string
  linkedinUsername: string
  location: string
  profilePicutre: string
  enrichmentData?: {
    firstName: string
    lastName: string
    fullName: string
    jobTitle: string
    summary: string
    workExperince: Array<{
      company: {
        id?: number
        name: string
        logo: string
        url: string
        employees: string
      }
      date: {
        start: {
          month?: number
          day?: number | null
          year: number
        }
        end?: {
          month?: number
          day?: number | null
          year: number
        }
      }
      profile_positions: Array<{
        location?: string
        date: {
          start: {
            month?: number
            day?: number | null
            year: number
          }
          end?: {
            month?: number
            day?: number | null
            year: number
          }
        }
        company: string
        description?: string
        title: string
        employment_type: string
      }>
    }>
    education: Array<{
      date: {
        start: {
          month?: number | null
          day?: number | null
          year: number
        }
        end: {
          month?: number | null
          day?: number | null
          year: number
        }
      }
      school: {
        name: string
        logo?: string | null
        url?: string | null
      }
      degree_name?: string | null
      field_of_study: Array<string>
      grade?: string | null
    }>
    languages: Array<{
      name: string
      proficiency: string
    }>
    currentJobYear: number
    currentJobMonth: number
    location: {
      country: string
      country_code: string
      state?: string | null
      city: string
      timezone: string
      timezone_offset: string
      postal_code?: string | null
      address: string
    }
  }
  enrichmentDataSync: Date
}

type Attendee = {
  attendeeID: number
  personID: number
  eventID: number
  status: 'accepted' | 'declined' | 'tentative' | 'needsAction'
  removed: boolean
  lastSync: Date
  Person: Person
}

type Event = {
  eventID?: number
  userID?: number
  calendarID?: number
  externalID?: string
  iCalUID?: string
  title?: string
  description?: string
  status?: string
  timezone?: string
  start: Date
  end: Date
  externalEnrichmentID?: string
  emailNotification?: Date
  lastUpdate?: Date
  Attendees?: Attendee[]
}

const attendeeStatus = {
  accepted: {
    color: 'green',
    text: 'Accepted',
    icon: 'mdi-check'
  },
  declined: {
    color: 'red',
    text: 'Declined',
    icon: 'mdi-close'
  },
  tentative: {
    color: 'orange',
    text: 'Tentative',
    icon: 'mdi-help'
  },
  needsAction: {
    color: 'blue',
    text: 'Has not responded',
    icon: 'mdi-alert'
  }
}

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const event: Ref<Event> = ref({
  start: new Date(),
  end: new Date()
})

// Dialog state variables
const showLinkedInDialog = ref(false)
const selectedPerson = ref<Person | null>(null)

const timestampFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  weekday: 'long',
  hour12: true
})

const fetchData = async () => {
  loading.value = true
  const result = await myRequest('GET', `/app/event/${route.params.eventID}`)
  console.log(result)
  event.value = result
  loading.value = false
}

const navigateToPersonDetails = (eventId: number, personId: number, linkedinUsername: string | null) => {
  if (linkedinUsername && linkedinUsername !== 'notFound') {
    router.push(`/event/${eventId}/person/${personId}`)
  }
}

const handleAttendeeClick = (attendee: Attendee) => {
  if ([null, 'notFound'].includes(attendee.Person.linkedinUsername)) {
    selectedPerson.value = attendee.Person
    showLinkedInDialog.value = true
  } else {
    if (event.value.eventID && attendee.Person.personID) {
      navigateToPersonDetails(event.value.eventID, attendee.Person.personID, attendee.Person.linkedinUsername)
    }
  }
}

fetchData()
</script>

<template>
  <OGHeader :title="event.title || 'Event'" :subtitle="`Meeting with ${event?.Attendees?.length || 0} attendees`" />

  <div class="row q-gutter-md">
    <div class="col-12 col-md-6">
      <div class="myCard q-pa-md full-height" :showing="event.title">
        <div class="text q-mb-md">
          {{ timestampFormatter.format(new Date(event.start)) }} -
          {{ timestampFormatter.format(new Date(event.end)) }}
        </div>
        <div v-html="event.description" />
      </div>
    </div>
    <div class="col-12 col-md">
      <div class="myCard q-pa-md full-height">
        <h6>Attendees</h6>
        <q-list>
          <q-item
            clickable
            v-for="attendee in event.Attendees"
            :key="attendee.attendeeID"
            @click="handleAttendeeClick(attendee)"
          >
            <q-item-section avatar>
              <q-avatar>
                <q-tooltip>
                  {{ attendeeStatus[attendee.status].text }}
                </q-tooltip>
                <q-img v-if="attendee.Person.profilePicutre" :src="attendee.Person.profilePicutre" alt="avatar">
                  <template v-slot:error>
                    <q-icon name="mdi-account" size="lg" />
                  </template>
                </q-img>
                <q-icon v-if="!attendee.Person.profilePicutre" name="mdi-account" size="lg" />
                <q-badge floating :color="attendeeStatus[attendee.status].color">
                  <q-icon :name="attendeeStatus[attendee.status].icon" />
                </q-badge>
              </q-avatar>
            </q-item-section>
            <q-item-section>
              <q-item-label>
                <span :class="{ 'text-decoration-line-through': attendee.removed, 'cursor-pointer': true }">
                  {{
                    attendee.Person.firstName === '' && attendee.Person.lastName === ''
                      ? attendee.Person.email
                      : `${attendee.Person.firstName} ${attendee.Person.lastName}`
                  }}
                </span>
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </div>
    </div>
  </div>

  <!-- LinkedIn Profile Dialog -->
  <UpdatePersonDialog
    v-if="selectedPerson"
    v-model="showLinkedInDialog"
    :person="selectedPerson"
    :eventID="Number(route.params.eventID)"
    @search-complete="fetchData"
  />
</template>

<style scoped>
.text-decoration-line-through {
  text-decoration: line-through;
}
</style>
